import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import PopupWrapper from "../../components/PopupWrapper";
import ErrorBoundary from "../../components/ErrorBoundary";
import FormBox from "../../components/FormBox/FormBox";
import {
  ExistingPayoffRequestModal,
  LoadingButton,
} from "../../Utils/CustomComponent";
import urls from "../../Utils/urls";
import { commonToast } from "../../Utils";
import useFetchTableData from "../../useFetchTableData";
import { fetchPortfolioAtGlance } from "../../store/reducers/dashboardSlice/api";
import axiosInstance from "../../axios";
import { Search } from "react-bootstrap-icons";
import SearchLoan from "../../components/SearchLoan/SearchLoan";
import { roleType } from "../../Utils/constant";

const RequestPayOffModalLender = ({
  payOffModal,
  setPayOffModal,
  refreshTable,
}) => {
  const [disableBtn, setDisableBtn] = useState(false);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [showLoanSearch, setShowLoanSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [query2, setQuery2] = useState("");
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [duplicate, setDuplicate] = useState(false);
  const [duplicateDetails, setDuplicateDetails] = useState(null);
  const accountOptions = useSelector((state) => state.account.accountOptions);
  const selectedRole = useSelector((state) => state.account.selectedRole.value);
  const email = useSelector(
    (state) => state.RS_Authentication.user.details.email,
  );
  const masqueradeEmail = useSelector(
    (state) => state.RS_Authentication.masquerade,
  );

  // const { data, status } = useFetchTableData(
  //   fetchPortfolioAtGlance,
  //   "portfolioAtGlance",
  // );

  const createPayoffFormSchema = {
    resolver: yupResolver(
      Yup.object().shape({
        loan_number: Yup.string().required("Loan number is required!"),
        payoff_good_through_date: Yup.string().required(
          "Payoff Good Through Date is required!",
        ),
        cema: Yup.string().required("This field is required!"),
      }),
    ),
  };
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm(createPayoffFormSchema);

  const loanReqid = accountOptions.map((data) => data.value).join(" ");
  const roleCondition = selectedRole === roleType.broker ? 1 : 0;

  const payoff_good_through_date = watch("payoff_good_through_date");
  const selectedLoan = watch("loan_number");
  // const demand_expiration_date = payoff_good_through_date
  //   ? new Date(moment(payoff_good_through_date).add(7, "days"))
  //   : null;
  const userDetails = JSON.parse(localStorage.getItem("accessed_user"));

  const options =
    data?.map((el) => ({
      label: el.account,
      value: el.account,
      recid: el.LoanRecID,
    })) || [];

  const optionsForProperty =
    data?.map((el) => ({
      label: el?.["Property Address"],
      value: el.account,
      recid: el.LoanRecID,
    })) || [];

  const selectedLoanRecid = options?.filter(
    (el) => el.label === selectedLoan,
  )?.[0]?.recid;

  useEffect(() => {
    async function getCurrentDate() {
      try {
        const res = await axiosInstance.get(urls.borrower.currentDateNTime);
        setMinDate(res?.data?.datetime);

        const maxAllowedDate = new Date(res?.data?.datetime);
        maxAllowedDate.setDate(maxAllowedDate.getDate() + 30);

        setMaxDate(maxAllowedDate);
      } catch (error) {
        console.error(error);
      }
    }

    getCurrentDate();
  }, []);

  useEffect(() => {
    // Create an AbortController to cancel previous requests
    const abortController = new AbortController();

    const delayDebounce = setTimeout(() => {
      if (query.length >= 2 || query2.length >= 2) {
        fetchLoanResults(query, query2, abortController.signal); // Pass signal to the fetch request
      } else {
        if (!data.length) {
          setData([]);
        }
      }
    }, 300); // Debounce delay time

    // Cleanup function to cancel previous request if query changes
    return () => {
      clearTimeout(delayDebounce);
      abortController.abort(); // Cancel the previous request if it's still ongoing
    };
  }, [query, query2]);

  useEffect(() => {
    if (selected) {
      setValue("property_address", selected);
      setValue("loan_number", selected);
    }
  }, [selected, query, query2]);

  const fetchLoanResults = async (searchTerm, PropertyAddress, signal) => {
    setLoading(true);
    try {
      const payload = {
        Loanname: searchTerm,
        PropertyAddress: PropertyAddress,
        LenBroRecid: loanReqid,
        is_broker: roleCondition,
      };

      const response = await axiosInstance.post(
        urls.dashboard.postLoanLookUpActive(),
        payload,
        { signal: signal },
      );

      if (response?.data?.[0]) {
        setData(response?.data?.[0]);
      } else {
        setData([]);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.error("Previous POST request aborted");
      } else {
        console.error("Error fetching loan results:", error);
        setData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const demandExpirationDate = useMemo(() => {
    return payoff_good_through_date
      ? new Date(moment(payoff_good_through_date).add(7, "days"))
      : null;
  }, [payoff_good_through_date]);

  useEffect(() => {
    setValue("demand_expiration_date", demandExpirationDate);
  }, [demandExpirationDate]);

  function closeModal() {
    setPayOffModal(false);
    setQuery("");
    setQuery2("");
    setData([]);
    setSelected("");
    reset({
      remarks: "",
    });
  }

  async function onSubmit(data) {
    setDisableBtn(true);
    const id = toast.loading("Creating payoff request");

    const res = await axiosInstance.get(urls.borrower.currentDateNTime);

    const now = moment
      .tz(res?.data?.datetime, "America/Los_Angeles")
      .format("MM/DD/YYYY");

    // data.subject = `PO demand good thru ${data.payoff_good_through_date}  ${data.loan_number}`;
    // data.incident_description = `PO demand good thru ${data.payoff_good_through_date}  ${data.loan_number}`;
    // data.category_id = "38298369716579832700126775148817329199";
    // data.account = selectedAccount?.value;
    // data.department_id = "2";
    // data.sub_category_id = "42888371779545365743097884625194376948";
    // data.user_id = `${userDetails.user_id}`;
    // data.contact_email = userDetails.email;
    // data.status_id = "1";
    // data.role_type = selectedRole;
    // data.severity_id = "";
    // data.urgency_id = "";

    const payload = {
      loanrecid: selectedLoanRecid,
      loan_number: data.loan_number,
      payoff_good_through_date: data.payoff_good_through_date,
      demand_expiration_date: moment(data.demand_expiration_date).format(
        "MM/DD/YYYY",
      ),
      requesting_party_email: userDetails.email,
      source: "Loanlinq",
      payoff_requested_date: now,
      remarks: data.remarks,
      role: "Lender",
      logger_email:
        masqueradeEmail?.isIt == true
          ? masqueradeEmail?.p_logged_in_email
          : email,
      cema: data.cema,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: "Basic UkVTVEFQSUxPQU5MSU5ROjg4V2VIZWxwVSQjQA==",
      // maxBodyLength: Infinity,
    };
    axiosInstance
      .post(urls.support.createPayoff, payload, { headers })
      .then((response) => {
        if (
          response?.data?.message?.includes(
            "Active Payoff Request is already in process for this Loan",
          )
        ) {
          setDuplicate(true);
          setDuplicateDetails({ ...response?.data, loanNumber: selectedLoan });
          toast.update(id, {
            render: "Active Payoff Request is already in process for this Loan",
            type: "info",
            isLoading: false,
            ...commonToast,
          });
          setDisableBtn(false);
          closeModal();
        } else {
          closeModal();
          toast.update(id, {
            render: "Payoff Request Created",
            type: "success",
            isLoading: false,
            ...commonToast,
          });
          refreshTable();
          setDisableBtn(false);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.update(id, {
          render: "SOME ERROR OCCURED",
          type: "error",
          isLoading: false,
          ...commonToast,
        });
        setDisableBtn(false);
      });
  }

  return (
    <div>
      <PopupWrapper
        Component={
          <ErrorBoundary>
            <div>
              <FormBox
                options={payOffArr(
                  options,
                  optionsForProperty,
                  demandExpirationDate,
                  minDate,
                  setShowLoanSearch,
                  setQuery,
                  setQuery2,
                  loading,
                  setSelected,
                  maxDate
                )}
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors}
              />
              <div className="text-end">
                <LoadingButton
                  type="button"
                  className="btn btn-primary mt-5 text-white"
                  onClick={handleSubmit(onSubmit)}
                  title={"Submit Request"}
                  disabled={disableBtn}
                  loading={disableBtn}
                />
              </div>
            </div>
          </ErrorBoundary>
        }
        title="Request Payoff"
        extraClass="width-800"
        toShow={payOffModal}
        closeEventHandler={closeModal}
        titleClassName="p-3"
      />
      <ExistingPayoffRequestModal
        show={duplicate}
        onClose={() => setDuplicate(false)}
        loanNumber={duplicateDetails?.loanNumber}
        requestDate={duplicateDetails?.request_date}
        payoffRequestId={duplicateDetails?.request_number}
      />
      {/* 
      <SearchLoan
        show={showLoanSearch}
        onHide={() => setShowLoanSearch(false)}
        navigation={false}
        setLoan={(loan) => setValue("loan_number", loan?.account)}
      /> */}
    </div>
  );
};

export default RequestPayOffModalLender;

const payOffArr = (
  options,
  optionsForProperty,
  demandExpirationDate,
  minDate,
  setShowLoanSearch,
  setQuery,
  setQuery2,
  loading,
  setSelected,
  maxDate
) => {
  return [
    {
      title: (
        <>
          <Search className="me-3" /> Search loan number...
        </>
      ),
      field: "loan_number",
      type: "select",
      className: "col-6 py-5",
      options: options,
      onInputChange: true,
      setInputChangeQuery: setQuery,
      isLoading: loading,
      setSelected: setSelected,
      noOptionsMessage: () => "Search loan number",
      isClearable:true
    },
    {
      title: (
        <>
          <Search className="me-3" /> Search property address...
        </>
      ),
      field: "property_address",
      type: "select",
      className: "col-6 py-5",
      options: optionsForProperty,
      onInputChange: true,
      setInputChangeQuery: setQuery2,
      isLoading: loading,
      setSelected: setSelected,
      noOptionsMessage: () => "Search property address",
      isClearable:true
    },
    // {
    //   type: "content",
    //   className: "col-4 py-4 text-end align-items-center",
    //   content: (
    //     <button
    //       className="btn btn-primary p-4 px-5 w-100"
    //       onClick={() => setShowLoanSearch(true)}
    //     >
    //       <Search size={20} className="me-3 fw-bold" /> Find Loan
    //     </button>
    //   ),
    // },
    {
      title: "Payoff Good Through Date",
      field: "payoff_good_through_date",
      type: "date",
      className: "col-6 py-4",
      minDate: minDate,
      maxDate: maxDate,
      filter: true,
    },
    {
      title: "Demand Expiration Date",
      field: "demand_expiration_date",
      type: "date",
      className: "col-6 py-4",
      disable: true,
      value: demandExpirationDate,
    },
    {
      title: "CEMA",
      field: "cema",
      type: "select",
      className: "col-6 py-4",
      options: [
        { label: "Yes", value: "Y" },
        { label: "No", value: "N" },
      ],
    },
    {
      title: "Remarks (Optional)",
      field: "remarks",
      type: "textarea",
      className: "col-6 py-4",
      rows: 1,
    },
  ];
};
